import { createSelector } from 'redux-orm';

import orm from '../orm';

const getActiveCardModelsForList = ({ List, Card }, listId) => {
  if (!listId) {
    return [];
  }

  const listModel = List.withId(listId);
  if (!listModel || listModel.is_archive == true) {
    return [];
  }

  const boardModel = listModel.board;
  if (boardModel?.is_archive == true) {
    return [];
  }

  return Card.all()
    .filter({ listId })
    .toModelArray()
    .filter((cardModel) => cardModel.is_archive != true);
};

export const makeSelectCardAssigneesByListId = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    ({ List, Card }, listId) => {
      const result = {};

      getActiveCardModelsForList({ List, Card }, listId).forEach((cardModel) => {
        result[cardModel.id] = cardModel.users.toModelArray().map((userModel) => ({
          userId: userModel.id,
          name: userModel.name || userModel.username || userModel.email || String(userModel.id),
          organization: userModel.organization || '',
        }));
      });

      return result;
    },
  );

/** Карточки текущего списка без назначенных исполнителей (включая незакрытые). */
export const makeSelectCardsWithoutAssigneesByListId = () =>
  createSelector(
    orm,
    (_, listId) => listId,
    ({ List, Card }, listId) =>
      getActiveCardModelsForList({ List, Card }, listId)
        .filter((cardModel) => cardModel.users.toModelArray().length === 0)
        .map((cardModel) => ({
          cardId: cardModel.id,
          name: cardModel.name || '',
        })),
  );

/** Уникальные исполнители карточек списка. */
export const selectUniqueAssigneesByListId = createSelector(
  orm,
  (_, listId) => listId,
  ({ List, Card }, listId) => {
    const byId = {};

    getActiveCardModelsForList({ List, Card }, listId).forEach((cardModel) => {
      cardModel.users.toModelArray().forEach((userModel) => {
        if (!byId[userModel.id]) {
          byId[userModel.id] = {
            id: userModel.id,
            name: userModel.name || userModel.username || userModel.email || String(userModel.id),
            avatarUrl: userModel.avatarUrl,
          };
        }
      });
    });

    return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  },
);

export const selectBoardMemberUserIds = createSelector(
  orm,
  (_, boardId) => boardId,
  ({ Board }, boardId) => {
    if (!boardId) {
      return [];
    }

    const boardModel = Board.withId(boardId);
    if (!boardModel) {
      return [];
    }

    return boardModel.memberships.toRefArray().map((membership) => String(membership.userId));
  },
);

export const selectAssigneesMissingFromBoard = createSelector(
  orm,
  (_, listId) => listId,
  (_, __, boardId) => boardId,
  ({ List, Card, Board }, listId, boardId) => {
    if (!listId || !boardId) {
      return [];
    }

    const boardModel = Board.withId(boardId);
    // Без загруженной доски / memberships не считаем никого «отсутствующим» —
    // иначе ложный список (в ORM часто только текущий пользователь).
    if (!boardModel || boardModel.isFetching !== false) {
      return [];
    }

    const memberIds = new Set(
      boardModel.memberships.toRefArray().map((membership) => String(membership.userId)),
    );

    const byId = {};
    getActiveCardModelsForList({ List, Card }, listId).forEach((cardModel) => {
      cardModel.users.toModelArray().forEach((userModel) => {
        const userId = String(userModel.id);
        if (!memberIds.has(userId) && !byId[userId]) {
          byId[userId] = {
            id: userModel.id,
            name: userModel.name || userModel.username || userModel.email || userId,
            avatarUrl: userModel.avatarUrl,
          };
        }
      });
    });

    return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  },
);

export default {
  makeSelectCardAssigneesByListId,
  makeSelectCardsWithoutAssigneesByListId,
  selectUniqueAssigneesByListId,
  selectBoardMemberUserIds,
  selectAssigneesMissingFromBoard,
};
